import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, Check, X, Star, ShieldCheck, ArrowRight, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import SeoHead from "../components/SeoHead";
import Icon from "../components/Icon";
import ServiceCard from "../components/ServiceCard";
import { LOCALITIES } from "../data/services";
import { useServices } from "../context/ServicesContext";
import { TESTIMONIALS, whatsappLink } from "../data/content";
import { Loader2 } from "lucide-react";

const INCLUDED = {
  default: ["Diagnosis & inspection", "Standard repair labour", "Basic consumables", "30-day service assurance"],
};
const EXCLUDED = ["Cost of major spare parts", "Additional units beyond quoted scope", "Civil / masonry work unless booked"];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getBySlug, services, loading } = useServices();
  const service = getBySlug(slug);

  if (loading && !service) {
    return <div className="grid place-items-center py-32"><Loader2 className="w-8 h-8 animate-spin text-brand-orange" /></div>;
  }

  if (!service) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900">Service not found</h1>
        <Button onClick={() => navigate("/")} className="mt-4 bg-brand-orange hover:bg-orange-600 text-white">Back to Home</Button>
      </div>
    );
  }

  const related = services.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <div>
      <SeoHead
        title={`${service.name} Near You | Book Online - ApnaFix`}
        description={`Book trusted ${service.name} near you in Kandivali, Malad, Borivali & Goregaon. Verified professionals, transparent pricing from ${service.priceStarts}.`}
      />
      {/* Hero */}
      <div className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <Link to="/#services" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> All services</Link>
          <div className="flex items-start gap-4">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-brand-orange text-white"><Icon name={service.icon} className="w-7 h-7" /></span>
            <div>
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">{service.badge}</span>
              <h1 className="mt-1 font-display text-2xl sm:text-4xl font-extrabold tracking-tight">{service.name}</h1>
              <p className="mt-2 text-slate-300 max-w-xl">{service.desc}</p>
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5"><span className="text-slate-400">Starting at</span> <span className="font-mono-num font-bold text-white">{service.priceStarts}</span></span>
                <span className="inline-flex items-center gap-1.5 text-slate-300"><Clock className="w-4 h-4 text-orange-300" /> ~{service.avgTime}</span>
                <span className="inline-flex items-center gap-1.5 text-slate-300"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Verified pros</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_340px] gap-8">
        <div>
          <Tabs defaultValue="included">
            <TabsList>
              <TabsTrigger value="included" data-testid="service-tab-included">What's included</TabsTrigger>
              <TabsTrigger value="excluded" data-testid="service-tab-excluded">Not included</TabsTrigger>
            </TabsList>
            <TabsContent value="included" className="mt-4 space-y-2">
              {INCLUDED.default.map((i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-sm"><span className="grid place-items-center w-6 h-6 rounded-full bg-emerald-50 text-emerald-600"><Check className="w-4 h-4" /></span> {i}</div>
              ))}
            </TabsContent>
            <TabsContent value="excluded" className="mt-4 space-y-2">
              {EXCLUDED.map((i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-sm"><span className="grid place-items-center w-6 h-6 rounded-full bg-slate-100 text-slate-400"><X className="w-4 h-4" /></span> {i}</div>
              ))}
            </TabsContent>
          </Tabs>

          <h2 className="mt-10 font-display text-xl font-bold text-slate-900">Available across</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {LOCALITIES.map((l) => (
              <Link key={l.id} to={`/locations/${l.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-orange-400"><MapPin className="w-3.5 h-3.5 text-brand-orange" /> {l.name}</Link>
            ))}
          </div>

          <h2 className="mt-10 font-display text-xl font-bold text-slate-900">What customers say</h2>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {TESTIMONIALS.slice(0, 2).map((t) => (
              <div key={t.name} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex gap-0.5 text-amber-400 mb-2">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}</div>
                <p className="text-sm text-slate-700">"{t.text}"</p>
                <p className="mt-2 text-xs text-slate-500">— {t.name}, {t.area}</p>
              </div>
            ))}
          </div>

          <h2 className="mt-10 font-display text-xl font-bold text-slate-900">Related services</h2>
          <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {related.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
          </div>
        </div>

        {/* Sticky book sidebar */}
        <aside className="lg:sticky lg:top-24 h-max">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-widest font-bold text-brand-orange">Book in 60s</p>
            <p className="mt-1 font-mono-num text-2xl font-bold text-slate-900">{service.priceStarts} <span className="text-sm font-sans font-normal text-slate-400">onwards</span></p>
            <Button data-testid="service-book-now-sidebar" onClick={() => navigate(`/book?service=${service.slug}`)} className="mt-4 w-full bg-brand-orange hover:bg-orange-600 text-white font-bold h-12">Book {service.name} <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <a href={whatsappLink(`Hi ApnaFix, I need ${service.name}.`)} target="_blank" rel="noreferrer" className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold h-11 hover:bg-emerald-100">WhatsApp enquiry</a>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Verified professional</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Transparent pricing</li>
              <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-600" /> Quick arrival</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
